import { capitalize } from 'lodash';
import Info from '@mui/icons-material/Info';
import { STAT_TEXT, ORDERED_STATS_TOTAL } from 'lib/constants';
import { abbreviateNum } from 'lib/helpers';
import { Stats } from 'lib/types';
import { MobileTooltip } from 'components/MobileTooltip';

interface Props {
  title: string;
  subtitle: string;
  stats: Stats;
  deltas: Stats;
  infoText?: string;
  previousStats?: Stats;
}

export const StatWithDeltaCard = ({
  title,
  subtitle,
  stats,
  deltas,
  infoText,
  previousStats,
}: Props) => {
  const showNA = () => (
    <div className="pl-2 text-base font-bold text-gray-500">
      -%
    </div>
  );

  const showPercent = (n: number) => {
    const percent = Math.round(n * 1000.0) / 10.0;
    if (percent === 0.0) {
      return (
        <div className="pl-2 text-base font-bold text-gray-500">
          +0%
        </div>
      );
    }
    return (
      <div className={`pl-2 text-base font-bold ${n >= 0 ? 'text-green-700' : 'text-red'}`}>
        {`${n > 0 ? '+' : ''}${percent}`}
        %
      </div>
    );
  };

  const renderInfo = () => {
    if (!infoText) {
      return null;
    }
    return (
      <MobileTooltip title={infoText} className="ml-1">
        <Info fontSize="inherit" color="secondary" />
      </MobileTooltip>
    );
  };

  return (
    <div className="flex flex-wrap justify-start border p-4 rounded-lg bg-white dark:bg-appbg-dark shadow-sm my-4">
      <div className="flex pr-6 pb-2 w-full lg:w-1/5">
        <div>
          <div className="text-xl font-bold uppercase">
            {title}
            {renderInfo()}
          </div>
          <div className="text-l" suppressHydrationWarning>{subtitle}</div>
        </div>
      </div>
      <div className="flex flex-wrap justify-evenly gap-4 w-full lg:w-4/5">
        {ORDERED_STATS_TOTAL.map((key) => {
          const text = STAT_TEXT[key];
          const stat = stats[key as keyof typeof stats] || 0;
          const previousStat = previousStats?.[key as keyof typeof previousStats] || 0;
          const delta = deltas[key as keyof typeof deltas];
          return (
            <div className="pr-4 lg:px-2" key={key}>
              <div className="font-semibold text-l">
                {capitalize(text)}
              </div>
              <MobileTooltip title={`vs ${abbreviateNum(previousStat || 0, 1, true)} ${title.toLowerCase()}`}>
                <div className="text-2xl">
                  {abbreviateNum(stat, 1, true)}
                  {(stat < 0 || previousStat < 0) ? showNA() : showPercent(delta)}
                </div>
              </MobileTooltip>
            </div>
          );
        })}
      </div>
    </div>
  );
};
